import { CustomerOrderContextMenuComponentsProps } from "src/tenantCustomizations/TenantCustomizations.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { faBusinessTime, faCheck, faHandHoldingDollar, faHistory, faPrint } from "@fortawesome/pro-regular-svg-icons";
import i18n from "i18next";
import dayjs from "dayjs";
import { SalvosCustomerOrderApi } from "src/api/generated/tenants/salvos/erp/sales/customerOrder/salvosCustomerOrderApi.ts";
import { SalvosDeliveryContentPrintoutApi } from "src/api/generated/tenants/salvos/erp/sales/printout/salvosDeliveryContentPrintoutApi.ts";
import { dayJsToDateIsoStringNullable } from "src/utils/dayjsUtils.ts";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { useContext } from "react";
import { CustomerOrdersPageContext } from "src/components/views/erp/sales/customerOrder/CustomerOrdersPageContext.tsx";
import { AavoMenuDivider } from "src/components/common/menu/AavoMenuDivider.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { SalvosCustomerOrderTenantStateHistoryView } from "src/tenantCustomizations/tenants/salvos/sales/SalvosCustomerOrderTenantStateHistoryView.tsx";
import { downloadFile } from "src/utils/fileDownloading.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export const SalvosCustomerOrderContextMenuComponents = ({
	refreshData,
	onlySingleRowSelected,
	row,
}: CustomerOrderContextMenuComponentsProps) => {
	const { openDialog } = useGenericDialog();
	const showInputDialog = useInputDialog();
	const customerOrderId = row.customerOrderId;

	const customerOrdersPageContext = useContext(CustomerOrdersPageContext);
	const attributeViewRefreshRef = customerOrdersPageContext?.attributeViewRefreshRef;

	return [
		<AavoMenuDivider key={"dividerSalvos"} />,
		onlySingleRowSelected && (
			<AsyncMenuButton
				key={"printDeliveryContent"}
				icon={faPrint}
				label={i18n.t("delivery_content")}
				onClick={async () => {
					const fileHandle = await SalvosDeliveryContentPrintoutApi.getDeliveryContentPrintout({
						customerOrderId: customerOrderId,
					});
					downloadFile(fileHandle);
				}}
			/>
		),
		onlySingleRowSelected && (
			<AsyncMenuButton
				key={"setBuildingLicenceDate"}
				icon={faHistory}
				label={i18n.t("set_building_licence_date")}
				onClick={async () => {
					const buildingLicenceDate = await showInputDialog({
						title: i18n.t("building_licence_got_date"),
						defaultValue: dayjs().local(),
						type: "date",
						fieldLabel: i18n.t("select_date"),
						required: false,
					});
					if (buildingLicenceDate === undefined) return;
					await SalvosCustomerOrderApi.setCustomerOrderBuildingLicenceDate({
						customerOrderId: customerOrderId,
						buildingLicenceDate: dayJsToDateIsoStringNullable(buildingLicenceDate),
					});
					await refreshData();
					attributeViewRefreshRef?.refresh();
				}}
			/>
		),
		onlySingleRowSelected && (
			<AsyncMenuButton
				key={"setDeliveryDateAgreedDateButton"}
				label={i18n.t("set_delivery_date_agreed_date")}
				icon={faBusinessTime}
				onClick={async () => {
					const deliveryDateAgreedDate = await showInputDialog({
						title: i18n.t("delivery_date_agreed_date"),
						defaultValue: dayjs().local(),
						type: "date",
						fieldLabel: i18n.t("select_date"),
						required: false,
					});
					if (deliveryDateAgreedDate === undefined) return;
					await SalvosCustomerOrderApi.setDeliveryDateAgreedDate({
						customerOrderId: customerOrderId,
						deliveryDateAgreedDate: dayJsToDateIsoStringNullable(deliveryDateAgreedDate),
					});
					await refreshData();
					attributeViewRefreshRef?.refresh();
				}}
			/>
		),
		onlySingleRowSelected && (
			<AsyncMenuButton
				key={"setRevenueRecognitionDate"}
				label={i18n.t("set_revenue_recognition_date")}
				icon={faHandHoldingDollar}
				onClick={async () => {
					const revenueRecognitionDate = await showInputDialog({
						title: i18n.t("revenue_recognition_date"),
						defaultValue: dayjs().local(),
						type: "date",
						fieldLabel: i18n.t("select_date"),
						required: false,
					});
					if (revenueRecognitionDate === undefined) return;
					await SalvosCustomerOrderApi.setRevenueRecognitionDate({
						customerOrderId: customerOrderId,
						revenueRecognitionDate: dayJsToDateIsoStringNullable(revenueRecognitionDate),
					});
					await refreshData();
					attributeViewRefreshRef?.refresh();
				}}
			/>
		),
		onlySingleRowSelected && row.tenantState === "delivered" && (
			<AsyncMenuButton
				key={"markCustomerOrderToCompletedButton"}
				label={i18n.t("mark_completed")}
				icon={faCheck}
				onClick={async () => {
					await SalvosCustomerOrderApi.markCustomerOrderToCompleted({
						customerOrderId: customerOrderId,
					});
					await refreshData();
					attributeViewRefreshRef?.refresh();
				}}
			/>
		),
		onlySingleRowSelected && (
			<AsyncMenuButton
				key={"showStateHistory"}
				icon={faHistory}
				label={i18n.t("state_history")}
				onClick={async () => {
					openDialog({
						title: i18n.t("state_history"),
						size: "md",
						agentContext: {
							key: defineAgentUiContextKey("tenant.salvos.customerOrderStateHistory.dialog"),
							data: { customerOrderId: row.customerOrderId },
							pathSegments: [i18n.t("state_history")],
						},
						content: <SalvosCustomerOrderTenantStateHistoryView customerOrderId={row.customerOrderId} />,
					});
				}}
			/>
		),
	];
};
